import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { appConfig } from "../config/app.config";
import { QuizEvent } from "../enums/quiz-event.enum";
import { IPostMessage } from "../interfaces/post-message.interface";
import { replaceHeaderPhone } from "../redux/content-slice";
import { setQuizWasOpened, setQuizWasStarted } from "../redux/quiz-slice";
import { addExtraParams, addLeadExtraParams, setInitialPage, updateState } from "../redux/service-slice";
import { YaMetrikaCounter } from "../ya-metrika";
import { analyticsService } from '../analytics.service';
import { AnalyticsEvent } from "../enums/analytics-event.enum";
import { IPluginData } from '../interfaces/plugin-data.interface';
import { SceneToken } from "../enums/scene-token.enum";
import { useAppSelector } from "./redux.hook";
import { Scene } from '../interfaces/scene.interface';
import { browserHistoryService, quizHistoryService } from "../services/history.service";
import { BrowserHistoryCommand } from "../enums/browser-history-command.enum";

export function usePluginData() {
  const dispatch = useDispatch();
  const { wasStarted } = useAppSelector(state => state.quiz);
  const { questions } = useAppSelector(state => state.content.currentContent);

  const setFirstScene = useCallback((token: SceneToken) => {
    let firstScene: Scene = { token };
    if (token == SceneToken.QUESTION) {
      firstScene.subState = { 
        data: { 
          id: questions && questions[0]?.id, 
          distributor: questions && questions[0]?.distributor, 
          index: 0 
        }
      }
    }
    
    quizHistoryService.replace(firstScene);
    browserHistoryService.perform(BrowserHistoryCommand.REPLACE, firstScene);
    dispatch(setInitialPage(firstScene.token));
  }, [questions, dispatch]);

  const handlePostMessage = useCallback(({ data }: any) => {
    if (!data) return;

    try {
      var message:IPostMessage = JSON.parse(data);
    } catch (e) { return }
    
    if (message?.access_key != appConfig().access_key) return;

    switch (message.event) {
      case QuizEvent.SET_PLUGIN_DATA: {
        dispatch(updateState(message.data as IPluginData));
        return;
      }

      case QuizEvent.OPEN: {
        const page:SceneToken = message.data.page;

        if (!wasStarted) {
          setFirstScene(page || 0);
        }

        if (page == SceneToken.QUESTION && !wasStarted) {
          dispatch(setQuizWasStarted());
          analyticsService.send(AnalyticsEvent.START);
        }

        dispatch(setQuizWasOpened());
        analyticsService.send(AnalyticsEvent.OPEN, { trigger: message.data.trigger });
        return;
      }

      case QuizEvent.SET_HEADER_PHONE: {
        const { phone } = message.data;
        if (phone) dispatch(replaceHeaderPhone(phone));
        return;
      }

      case QuizEvent.SEND_LEAD_EXTRA_PARAMS: {
        dispatch(addLeadExtraParams(message.data));
        return;
      }

      case QuizEvent.SEND_EXTRA_PARAMS: {
        dispatch(addExtraParams(message.data));
        return;
      }

      case QuizEvent.ENABLE_WEBVISOR: {
        const counter = new YaMetrikaCounter();
        counter.init(message.data.counter, message.data.domain);
        return;
      }

      case QuizEvent.CHANGE_HISTORY: {
        const scene = message.data.scene as Scene;
        browserHistoryService.addToQueue(scene);
        return;
      }

      default: return;
    }
  }, [dispatch, wasStarted, setFirstScene]);

  useEffect(() => {
    window.removeEventListener('message', handlePostMessage);
    window.addEventListener('message', handlePostMessage);

    return () => window.removeEventListener('message', handlePostMessage);
  }, [handlePostMessage]);
}